import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, MenuItem, TextField, ThemeProvider, Typography, useMediaQuery } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faPlug, faArrowLeft, faGlobe } from '@fortawesome/free-solid-svg-icons';
import AppSidebar from './AppSidebar';
import { AccountUserMenu } from './UserMenu';
import { OVERVIEW_NAV } from '../constants/overviewNav';
import { SUPPORTED_LANGUAGES } from '../translations';
import { redirectToLogin } from '../utils/session';
import { clearRole, clearWorkspaceScopedSelections } from '../utils/permissions';
import './AppHeader.css';
import '../pages/sectionPages.css';
import '../pages/McpConnections.css';

// Scoped to security pages: matches Team/Administration and the existing portal
// chrome without changing the theme of unrelated screens.
export const portalSecurityTheme = createTheme({
  palette: { primary: { main: '#5616ea', dark: '#4711c4' }, text: { primary: '#20202a', secondary: '#6b7280' }, divider: '#e5e7eb' },
  typography: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, h1: { fontSize: '1.5rem', fontWeight: 700 }, h2: { fontSize: '1.125rem', fontWeight: 600 }, button: { textTransform: 'none', fontWeight: 600 } },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { borderRadius: 8, minHeight: 40 } } },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#515151', borderWidth: 1 } } } },
    MuiFormControlLabel: { styleOverrides: { label: { fontSize: 14 } } },
    MuiAlert: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 12 } } },
  },
});

export function McpTheme({ children }) {
  return <ThemeProvider theme={portalSecurityTheme}>{children}</ThemeProvider>;
}

export function McpLoading() {
  const { t } = useTranslation();
  return <Box role="status" className="mcp-loading"><CircularProgress size={22} aria-hidden="true" /><span>{t('mcp_loading')}</span></Box>;
}

export function McpLanguage() {
  const { t, i18n } = useTranslation();
  return <TextField select size="small" className="mcp-language" value={(i18n.language || 'en').split('-')[0]}
    SelectProps={{ inputProps: { 'aria-label': t('language') } }}
    onChange={e => { i18n.changeLanguage(e.target.value); localStorage.setItem('language', e.target.value); }}
    InputProps={{ startAdornment: <FontAwesomeIcon icon={faGlobe} aria-hidden="true" style={{ marginRight: 8 }} /> }}>
    {SUPPORTED_LANGUAGES.map(language => <MenuItem key={language.code} value={language.code}>{language.label}</MenuItem>)}
  </TextField>;
}

export function McpAuthFrame({ children, cardClassName = '' }) {
  const { t } = useTranslation();
  return <McpTheme><div className={`mcp-auth-shell${cardClassName ? ` has-${cardClassName}` : ''}`}>
    <header className="mcp-auth-header"><Link to="/my-bots" aria-label="Ultimo Bots"><img src="/ultimo-bots-white.png" alt="Ultimo Bots" /></Link><McpLanguage /></header>
    <main className={`mcp-auth-card ${cardClassName}`.trim()}>{children}</main>
    <footer className="mcp-auth-footer"><a href="https://ultimo-bots.com/privacy" target="_blank" rel="noopener noreferrer">{t('footer_privacy')}</a><span>Ultimo Bots</span><a href="https://ultimo-bots.com/terms" target="_blank" rel="noopener noreferrer">{t('footer_terms')}</a></footer>
  </div></McpTheme>;
}

export function McpPortalLayout({ children }) {
  const { t } = useTranslation();
  const mobile = useMediaQuery('(max-width:768px)');
  const [open, setOpen] = useState(false);
  const pages = OVERVIEW_NAV.some(item => item.path === '/mcp-connections') ? OVERVIEW_NAV : [...OVERVIEW_NAV, { label: t('mcp_connections_title'), path: '/mcp-connections', icon: faPlug }];
  const logout = () => { clearWorkspaceScopedSelections(); localStorage.removeItem('webflow_connected'); localStorage.removeItem('webflow_history'); redirectToLogin({ clearRole }); };
  return <McpTheme><div className="app-shell mcp-portal-shell">
    <header className="app-header">
      <div className="app-header-left">
        <button type="button" className="app-header-burger" aria-label={t('header_toggle_menu')} aria-expanded={open} onClick={() => setOpen(!open)}><FontAwesomeIcon icon={faBars} /></button>
        <Link className="app-header-logo" to="/my-bots" aria-label="Ultimo Bots"><img src="/ultimo-bots-logo-white.png" alt="Ultimo Bots" /></Link>
        <span className="mcp-header-label">{t('mcp_connections_title')}</span>
      </div>
      <div className="app-header-right"><AccountUserMenu onLogout={logout} /></div>
    </header>
    <AppSidebar pages={pages} selectedIndex={{ mainIndex: pages.findIndex(item => item.path === '/mcp-connections'), nestedIndex: null }} setSelectedIndex={() => setOpen(false)} t={t} visible={!mobile || open} isMobile={mobile} showWorkspaceSwitcher={false} />
    <div className={`app-sidebar-backdrop${open ? ' show' : ''}`} onClick={() => setOpen(false)} />
    <main className="app-content"><div className="mcp-management section-shell">{children}</div></main>
  </div></McpTheme>;
}

export function McpIntro({ title, subtitle }) {
  return <header className="section-head"><Typography component="h1" variant="h1">{title}</Typography><Typography variant="body2" className="section-head-sub">{subtitle}</Typography></header>;
}

export function McpBackLink() {
  const { t } = useTranslation();
  return <Button component={Link} to="/mcp-connections" startIcon={<FontAwesomeIcon icon={faArrowLeft} />}>{t('mcp_connections_title')}</Button>;
}

export function McpSessionRecovery({ onRetry }) {
  const { t } = useTranslation();
  return <McpAuthFrame><McpIntro title={t('mcp_signin_title')} subtitle={t('mcp_signin_subtitle')} /><Alert severity="error" sx={{ my: 2 }}>{t('mcp_error')}</Alert><Button variant="contained" onClick={onRetry}>{t('mcp_retry')}</Button></McpAuthFrame>;
}
